/**
 * Session lifecycle state machine (pure).
 *
 * Linear happy path (face-to-face):
 *   scheduled → reminded → check-in-available → checked-in (in-progress)
 *     → [tracking] → checkout-reminded → checked-out (completed) → synced
 *
 * Telehealth (faceToFace === false) sessions skip the GPS-bound states
 * (`check-in-available`, `tracking`): there is no GPS check-in and no
 * kilometre tracking.
 */

import type { Session, SessionLifecycleState } from './types'
import { isCallbackSession } from './callback-session'
import { isFaceToFace } from './telehealth'

/** States that only make sense for an in-person (GPS) session. */
export const GPS_BOUND_STATES: ReadonlySet<SessionLifecycleState> = new Set<SessionLifecycleState>([
  'check-in-available',
  'tracking',
])

/**
 * The full (face-to-face) transition graph. Each key lists the states it may
 * transition to directly.
 */
const TRANSITIONS: Readonly<Record<SessionLifecycleState, readonly SessionLifecycleState[]>> = {
  scheduled: ['reminded', 'check-in-available', 'cancelled', 'not-started'],
  reminded: ['check-in-available', 'cancelled', 'not-started'],
  'check-in-available': ['checked-in', 'cancelled', 'not-started'],
  'checked-in': ['tracking', 'checkout-reminded', 'checked-out', 'cancelled'],
  tracking: ['checkout-reminded', 'checked-out', 'cancelled'],
  'checkout-reminded': ['checked-out', 'cancelled'],
  'checked-out': ['synced'],
  synced: [],
  cancelled: [],
  // Not fully terminal: check-in is a side event (like from `scheduled`),
  // not a graph edge, so a later manual correction still works.
  'not-started': ['cancelled'],
}

/** Terminal lifecycle state — nothing follows `synced`. */
export const TERMINAL_STATE: SessionLifecycleState = 'synced'

/** States from which nothing can follow (`synced` happy path, `cancelled` side exit). */
export const TERMINAL_STATES: ReadonlySet<SessionLifecycleState> = new Set<SessionLifecycleState>([
  'synced',
  'cancelled',
])

/**
 * Pure transition check against the general (face-to-face) graph. Does not
 * apply per-session guards — use {@link canTransitionFor} for that.
 */
export function canTransition(from: SessionLifecycleState, to: SessionLifecycleState): boolean {
  return TRANSITIONS[from]?.includes(to) ?? false
}

/**
 * Telehealth guard: in-person-only states are not reachable, and the graph is
 * "rewired" so that transitions which would have landed on a GPS-bound state
 * instead fall through to the next reachable non-GPS state.
 *
 * Concretely, for telehealth:
 *   scheduled → reminded → checked-in → checkout-reminded → checked-out → synced
 */
function telehealthNextStates(from: SessionLifecycleState): SessionLifecycleState[] {
  const resolved = new Set<SessionLifecycleState>()
  const visit = (state: SessionLifecycleState) => {
    for (const next of TRANSITIONS[state] ?? []) {
      if (GPS_BOUND_STATES.has(next)) {
        // Skip the GPS-bound state and inherit what it could reach.
        visit(next)
      } else {
        resolved.add(next)
      }
    }
  }
  visit(from)
  return [...resolved]
}

/**
 * The states a session may move to next, applying per-session guards.
 * Returns an empty array for terminal/GPS-bound-from-telehealth dead ends.
 */
export function nextStatesFor(session: Pick<Session, 'lifecycleState' | 'faceToFace'>): SessionLifecycleState[] {
  const { lifecycleState } = session
  if (isFaceToFace(session)) {
    return [...TRANSITIONS[lifecycleState]]
  }
  // A telehealth session should never *be* in a GPS-bound state, but guard
  // defensively: from such a state, route forward to non-GPS states.
  return telehealthNextStates(lifecycleState)
}

/**
 * Session-aware transition check. Applies the telehealth guard so callers can
 * validate a proposed transition for a specific session.
 */
export function canTransitionFor(
  session: Pick<Session, 'lifecycleState' | 'faceToFace' | 'source' | 'callback'>,
  to: SessionLifecycleState,
): boolean {
  if (isCallbackSession(session)) return false
  if (!isFaceToFace(session) && GPS_BOUND_STATES.has(to)) return false
  return nextStatesFor(session).includes(to)
}

/** Whether the session has reached a terminal state (`synced` or `cancelled`). */
export function isTerminal(state: SessionLifecycleState): boolean {
  return TERMINAL_STATES.has(state)
}

/* ------------------------------ undo check-in ------------------------------ */
//
// "Undo instead of confirm" (owner decision 2026-10-07): check-in has no
// confirmation dialog; instead the provider gets a short UNDO window after the
// tap. Undo is a deliberate, server-validated ROLLBACK transition — kept out
// of the forward TRANSITIONS graph above (like `departure-end`, it is an
// explicitly documented side transition, not part of the happy path). It
// restores the pre-check-in lifecycle state while journey state (`arrived`)
// is deliberately left intact — the provider is still at the address.

/**
 * How long the client offers UNDO after a MANUAL session start (snackbar).
 * Auto (dwell) starts get the longer UNDO_AUTO_START_WINDOW_MS — see
 * shared/sessions/auto-start.ts.
 */
export const UNDO_CHECK_IN_WINDOW_MS = 15_000

/**
 * Extra server-side acceptance beyond the client window, covering submit
 * latency on a tap made at the very end of the 15 s.
 */
export const UNDO_CHECK_IN_SERVER_GRACE_MS = 15_000

/**
 * States an `undo-check-in` may be applied from. `tracking` is included
 * because a km-update inside the undo window legally advances
 * `checked-in → tracking` before the provider taps UNDO.
 */
export const UNDO_CHECK_IN_ALLOWED_FROM: ReadonlySet<SessionLifecycleState> =
  new Set<SessionLifecycleState>(['checked-in', 'tracking'])

/**
 * Where undo lands the lifecycle. In-person sessions return to the GPS-bound
 * `check-in-available`; telehealth (which can never hold a GPS-bound state)
 * returns to `reminded`. Both are valid check-in sources, so re-checking-in
 * works immediately.
 */
export function undoCheckInRestoredState(
  session: Pick<Session, 'faceToFace'>,
): SessionLifecycleState {
  return isFaceToFace(session) ? 'check-in-available' : 'reminded'
}

/**
 * Server-side window check: undo is only valid shortly after the check-in
 * being undone (`actualStart`). No recorded check-in time → no undo.
 */
export function isUndoCheckInWindowOpen(
  actualStart: number | null | undefined,
  now: number = Date.now(),
  windowMs: number = UNDO_CHECK_IN_WINDOW_MS + UNDO_CHECK_IN_SERVER_GRACE_MS,
): boolean {
  if (actualStart == null || !Number.isFinite(actualStart)) return false
  return now - actualStart <= windowMs && now >= actualStart - 60_000
}

/**
 * How long the client offers UNDO after a departure record (check-out).
 * Deliberately LONGER than the manual check-in window: a departure record is
 * a terminal, billing-relevant action with a heavier teardown (2026-10-07
 * field feedback: "way too easy to accidentally end the session, and I had no
 * way of undoing it").
 */
export const UNDO_CHECK_OUT_WINDOW_MS = 60_000

/** Extra server-side acceptance beyond the client window (submit latency). */
export const UNDO_CHECK_OUT_SERVER_GRACE_MS = 15_000

/**
 * States an `undo-check-out` may be applied from. ONLY `checked-out`: once a
 * session reaches the terminal `synced` state the record has moved on — an
 * undo past that gets an honest 409.
 */
export const UNDO_CHECK_OUT_ALLOWED_FROM: ReadonlySet<SessionLifecycleState> =
  new Set<SessionLifecycleState>(['checked-out'])

/**
 * Where a departure undo lands the lifecycle: back to `checked-in` — a legal
 * check-out source for both in-person and telehealth sessions. A subsequent
 * km-update legally re-advances `checked-in → tracking`.
 */
export const UNDO_CHECK_OUT_RESTORED_STATE: SessionLifecycleState = 'checked-in'

/**
 * Server-side window check for a departure undo, keyed to the recorded
 * departure time (`actualEnd`). No recorded departure → no undo.
 */
export function isUndoCheckOutWindowOpen(
  actualEnd: number | null | undefined,
  now: number = Date.now(),
  windowMs: number = UNDO_CHECK_OUT_WINDOW_MS + UNDO_CHECK_OUT_SERVER_GRACE_MS,
): boolean {
  if (actualEnd == null || !Number.isFinite(actualEnd)) return false
  return now - actualEnd <= windowMs && now >= actualEnd - 60_000
}
